import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Button, notification, Upload } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import {
  HiOutlineBuildingOffice2,
  HiOutlinePhone,
} from "react-icons/hi2";
import { HiOutlineMail } from "react-icons/hi";
import { LuUpload, LuX } from "react-icons/lu";
import { useSelector } from "react-redux";
import { Palette, SquareUser } from "lucide-react";
import {
  useCreateOrganizationMutation,
  useUploadFileMutation,
} from "@/store/api/apiSlice";
import { useUpdateOrganizationMutation } from "../../store/api/apiSlice";
import {
  ALLOWED_LOGO_EXTENSIONS,
  ALLOWED_LOGO_MIME_TYPES,
} from "../../constants/organization";
import { getMimeFromExtension } from "../../utils/getMimeFromExtension";
import OrganizationLocationInput from "../common/OrganizationLocationInput";
import {
  isSameOrganizationLocation,
  parseStoredLocation,
  toApiLocation,
} from "../../utils/organizationLocation";

const blankLegalFileLists = () => ({ termsDoc: [], privacyDoc: [] });

const AddOrganizationModal = ({ visible, onClose, editData, onSuccess }) => {
  const [form] = Form.useForm();

  const adminProfile = useSelector((s) => {
    const u = s.auth?.user;

    return u?.data || u || s.auth?.userData;
  });

  const [fileList, setFileList] = useState([]);
  const [logoUrl, setLogoUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [legalFileList, setLegalFileList] = useState(blankLegalFileLists);

  const [createOrganization, { isLoading: isCreating }] =
    useCreateOrganizationMutation();
  const [uploadFile] = useUploadFileMutation();
  const [updateOrganization, { isLoading: isUpdating }] =
    useUpdateOrganizationMutation();

  useEffect(() => {
    if (!visible) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [visible]);

  useEffect(() => {
    if (!visible) return;

    if (editData) {
      form.setFieldsValue({
        fullName: editData.user?.full_name || null,
        email: editData.user?.email || null,
        organizationName: editData.name,
        organizationEmail: editData.email,
        organizationPhone: editData.phone,
        organizationLocation: parseStoredLocation(editData.location),
        organizationLogo: editData.logo,
        organizationColor: editData.color || "#202835",
      });

      if (editData.logo) {
        setLogoUrl(editData.logo);
        setFileList([
          {
            uid: "-1",
            name: "Current Logo",
            status: "done",
            url: editData.logo,
          },
        ]);
      }

      setLegalFileList({
        termsDoc: editData.terms_condition
          ? [
              {
                uid: "-terms",
                name: "Terms & Conditions",
                status: "done",
                url: editData.terms_condition,
              },
            ]
          : [],
        privacyDoc: editData.privacy_policy
          ? [
              {
                uid: "-privacy",
                name: "Privacy Policy",
                status: "done",
                url: editData.privacy_policy,
              },
            ]
          : [],
      });
    } else {
      form.resetFields();
      setFileList([]);
      setLogoUrl("");

      const profileTerms = adminProfile?.terms_condition;
      const profilePrivacy = adminProfile?.privacy_policy;

      setLegalFileList({
        termsDoc: profileTerms
          ? [
              {
                uid: "-terms",
                name: "Terms & Conditions",
                status: "done",
                url: profileTerms,
              },
            ]
          : [],
        privacyDoc: profilePrivacy
          ? [
              {
                uid: "-privacy",
                name: "Privacy Policy",
                status: "done",
                url: profilePrivacy,
              },
            ]
          : [],
      });
    }
  }, [editData, visible, form]);

  const onFinish = async (values) => {
    try {
      let termsResolved = "";
      let privacyResolved = "";

      try {
        termsResolved = legalFileList.termsDoc?.length
          ? await resolveLegalDocUpload(
              legalFileList.termsDoc[0],
              "organisationTermsConditions",
              "Failed to upload terms document",
            )
          : "";

        privacyResolved = legalFileList.privacyDoc?.length
          ? await resolveLegalDocUpload(
              legalFileList.privacyDoc[0],
              "organisationPrivacyPolicy",
              "Failed to upload privacy policy",
            )
          : "";
      } catch {
        return;
      }

      if (editData?._id) {
        const initialValues = {
          name: editData.name,
          email: editData.email,
          phone: editData.phone,
          logo: editData.logo,
          color: editData.color || "#202835",
          location: editData.location,
          terms_condition: editData.terms_condition || "",
          privacy_policy: editData.privacy_policy || "",
          user: {
            full_name: editData.user?.full_name,
            email: editData.user?.email,
          },
        };

        const updatePayload = {};

        if (values.organizationName !== initialValues.name) {
          updatePayload.name = values.organizationName;
        }

        if (values.organizationEmail !== initialValues.email) {
          updatePayload.email = values.organizationEmail;
        }

        if (values.organizationPhone !== initialValues.phone) {
          updatePayload.phone = values.organizationPhone;
        }

        if (values.organizationColor !== initialValues.color) {
          updatePayload.color = values.organizationColor;
        }

        if (logoUrl !== initialValues.logo) {
          updatePayload.logo = logoUrl;
        }

        const nextLocation = toApiLocation(values.organizationLocation);

        if (
          nextLocation &&
          !isSameOrganizationLocation(nextLocation, initialValues.location)
        ) {
          updatePayload.location = nextLocation;
        }

        const userFullNameChanged =
          values.fullName !== initialValues.user?.full_name;
        const userEmailChanged = values.email !== initialValues.user?.email;

        if (userFullNameChanged || userEmailChanged) {
          updatePayload.user = {};

          if (userFullNameChanged) {
            updatePayload.user.full_name = values.fullName;
          }

          if (userEmailChanged) {
            updatePayload.user.email = values.email || null;
          }
        }

        const baselineTerms = initialValues.terms_condition || "";
        const baselinePrivacy = initialValues.privacy_policy || "";

        if (termsResolved !== baselineTerms) {
          updatePayload.terms_condition =
            termsResolved === "" ? null : termsResolved;
        }

        if (privacyResolved !== baselinePrivacy) {
          updatePayload.privacy_policy =
            privacyResolved === "" ? null : privacyResolved;
        }

        if (Object.keys(updatePayload).length > 0) {
          try {
            const result = await updateOrganization({
              id: editData._id,
              data: updatePayload,
            }).unwrap();

            notification.success({
              message: "Success",
              description: "Organisation updated successfully",
            });

            if (onSuccess) {
              onSuccess(result);
            }
          } catch (error) {
            notification.error({
              message: "Update Failed",
              description:
                error.data?.message ||
                "Failed to update organisation. Please try again.",
            });

            return;
          }
        } else {
          notification.info({
            message: "No Changes",
            description: "No changes were made to update.",
          });
        }
      } else {
        const createdLocation = toApiLocation(values.organizationLocation);
        const createPayload = {
          user: {
            full_name: values.fullName,
            email: values.email,
            password: values.password,
          },
          organization: {
            name: values.organizationName,
            email: values.organizationEmail,
            phone: values?.organizationPhone || "",
            logo: logoUrl || "",
            color: values.organizationColor,
            ...(createdLocation ? { location: createdLocation } : {}),
            ...(termsResolved ? { terms_condition: termsResolved } : {}),
            ...(privacyResolved ? { privacy_policy: privacyResolved } : {}),
          },
        };

        try {
          const result = await createOrganization(createPayload).unwrap();

          notification.success({
            message: "Success",
            description: "Organisation created successfully",
          });

          if (onSuccess) {
            onSuccess(result);
          }
        } catch (error) {
          notification.error({
            message: "Creation Failed",
            description:
              error.data?.message ||
              "Failed to create organisation. Please try again.",
          });

          return;
        }
      }

      onClose();

      form.resetFields();
      setFileList([]);
      setLogoUrl("");
      setLegalFileList(blankLegalFileLists());
    } catch (error) {
      notification.error({
        message: "Operation Failed",
        description: "An unexpected error occurred. Please try again.",
      });
    }
  };

  const handleFileUpload = async (file) => {
    try {
      setUploading(true);

      const formData = new FormData();
      const inferredMime = file.type || getMimeFromExtension(file.name);
      const normalizedFile =
        !file.type && inferredMime
          ? new File([file], file.name, { type: inferredMime })
          : file;
      formData.append("file", normalizedFile);

      const data = await uploadFile({ file: normalizedFile }).unwrap();

      if (data.file) {
        setLogoUrl(data.file);
        setFileList([
          {
            uid: "-1",
            name: file.name,
            status: "done",
            url: data.file,
          },
        ]);
        return data.file;
      }
      throw new Error("Upload failed");
    } catch (error) {
      notification.error({
        message: "Upload Failed",
        description: "Failed to upload logo. Please try again.",
      });

      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setFileList([]);
    setLogoUrl("");
    form.setFieldValue("organizationLogo", undefined);

    return true;
  };
  const validateLogoFile = (file) => {
    const inferredMime = file.type || getMimeFromExtension(file.name);
    const fileName = file.name?.toLowerCase() || "";
    const hasAllowedExtension = ALLOWED_LOGO_EXTENSIONS.some((ext) =>
      fileName.endsWith(ext),
    );
    const hasAllowedMime = ALLOWED_LOGO_MIME_TYPES.includes(inferredMime);

    if (!hasAllowedMime || !hasAllowedExtension) {
      notification.error({
        message: "Upload Error",
        description: "Only PNG, JPG/JPEG, and WEBP files are supported.",
      });
      return false;
    }

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      notification.error({
        message: "Upload Error",
        description: "Image must be smaller than 2MB!",
      });
      return false;
    }

    return true;
  };

  const handleLogoInputChange = async (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (!validateLogoFile(selectedFile)) {
      event.target.value = "";
      return;
    }

    await handleFileUpload(selectedFile);
    event.target.value = "";
  };

  const handleLogoPreview = (file) => {
    if (!file?.url) return;

    const previewWindow = window.open("", "_blank");
    if (!previewWindow) return;

    const previewImage = previewWindow.document.createElement("img");
    previewImage.src = file.url;
    previewImage.alt = file?.name || "Organisation logo preview";
    previewImage.style.maxWidth = "80vw";
    previewImage.style.maxHeight = "80vh";
    previewImage.style.height = "auto";
    previewImage.style.display = "block";
    previewImage.style.borderRadius = "16px";

    previewWindow.document.body.style.margin = "0";
    previewWindow.document.body.style.backgroundColor = "#fff";
    previewWindow.document.body.style.display = "flex";
    previewWindow.document.body.style.justifyContent = "center";
    previewWindow.document.body.style.alignItems = "center";
    previewWindow.document.body.style.minHeight = "100vh";
    previewWindow.document.body.appendChild(previewImage);
  };

  const resolveLegalDocUpload = async (
    fileEntry,
    filename,
    failureDescription,
  ) => {
    if (!fileEntry) return "";
    if (fileEntry.originFileObj) {
      try {
        const res = await uploadFile({
          file: fileEntry.originFileObj,
          filename,
        }).unwrap();

        return res?.file || "";
      } catch {
        notification.error({
          message: "Upload Failed",
          description: failureDescription,
        });
        throw new Error(failureDescription);
      }
    }

    return typeof fileEntry.url === "string" ? fileEntry.url : "";
  };

  const validatePassword = ({ getFieldValue }) => ({
    validator(_, value) {
      if (!value || getFieldValue("password") === value) {
        return Promise.resolve();
      }
      return Promise.reject(new Error("The two passwords do not match!"));
    },
  });

  const locationFormItem = (
    <Form.Item
      label="Organisation Location"
      name="organizationLocation"
      trigger="onChange"
      validateTrigger={["onBlur", "onSubmit"]}
      rules={[
        {
          validator: async (_, value) => {
            const parsed = parseStoredLocation(value);
            if (!parsed?.name) return;

            if (parsed.lat !== null && parsed.lng !== null) return;

            const initial = parseStoredLocation(editData?.location);
            if (editData && initial?.name === parsed.name) return;

            throw new Error(
              "Please select a location from the suggestions",
            );
          },
        },
      ]}
      className="mb-1 w-full md:col-span-2 relative z-50"
    >
      <OrganizationLocationInput />
    </Form.Item>
  );

  return (
    <Modal
      title={
        <div className="flex justify-between items-center">
          <span className="text-xl font-semibold">
            {editData ? "Edit Organisation" : "Add Organisation"}
          </span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      centered
      closeIcon={
        <div className="bg-gray-100 h-8 w-8 flex justify-center items-center rounded-full">
          <LuX className="text-red-400" size={20} />
        </div>
      }
      className="rounded-2xl organization-location-modal"
      classNames={{
        content: "organization-location-modal-content !overflow-visible",
        body: "!overflow-visible",
      }}
      styles={{
        content: { overflow: "visible" },
        body: { overflow: "visible" },
      }}
    >
      <Form
        form={form}
        layout="vertical"
        className="mt-0 flex max-h-[calc(100vh-140px)] flex-col overflow-visible [&_.ant-form-item-label]:!px-0 [&_.ant-form-item-label]:!pb-[2px] [&_.ant-form-item-label]:!pt-0 [&_.ant-form-item-label>label]:!h-auto [&_.ant-form-item-label>label]:!leading-normal [&_.ant-form-item-label>label]:!p-0 [&_.ant-input-prefix]:!w-5 [&_.ant-input-prefix]:!min-w-5 [&_.ant-input-prefix]:!justify-center [&_.ant-input-prefix]:!mr-3"
        onFinish={onFinish}
      >
        <div className="min-h-0 flex-1 overflow-visible pr-1">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <Form.Item
            label="Full Name"
            name="fullName"
            rules={[
              { required: true, message: "Please enter your full name!" },
            ]}
            className="mb-1"
          >
            <Input
              prefix={<SquareUser className="h-5 w-5 shrink-0 text-black" />}
              placeholder="Enter full name..."
              className="h-11"
            />
          </Form.Item>
          <Form.Item
            label="Email Address"
            name="email"
            rules={[
              { required: true, message: "Please enter your email!" },
              { type: "email", message: "Please enter a valid email!" },
            ]}
            className="mb-1"
          >
            <Input
              prefix={<HiOutlineMail className="h-5 w-5 shrink-0 text-black" />}
              placeholder="Enter email address..."
              className="h-11"
            />
          </Form.Item>
          {!editData && (
            <>
              <Form.Item
                label="Password"
                name="password"
                rules={[
                  { required: true, message: "Please enter your password!" },
                  {
                    min: 8,
                    message: "Password must be at least 8 characters!",
                  },
                ]}
                className="mb-1"
              >
                <Input.Password />
              </Form.Item>
              <Form.Item
                label="Confirm Password"
                name="confirmPassword"
                dependencies={["password"]}
                rules={[
                  { required: true, message: "Please confirm your password!" },
                  validatePassword,
                ]}
                className="mb-1"
              >
                <Input.Password />
              </Form.Item>
            </>
          )}
          <Form.Item
            label="Organisation Name"
            name="organizationName"
            rules={[
              { required: true, message: "Please enter Organisation name!" },
            ]}
            className="mb-1"
          >
            <Input
              prefix={
                <HiOutlineBuildingOffice2 className="h-5 w-5 shrink-0 text-black" />
              }
              placeholder="Enter organisation name..."
              className="h-11"
            />
          </Form.Item>
          <Form.Item
            label="Organisation Email Address"
            name="organizationEmail"
            rules={[
              { required: true, message: "Please enter Organisation email!" },
              { type: "email", message: "Please enter a valid email!" },
            ]}
            className="mb-1"
          >
            <Input
              prefix={<HiOutlineMail className="h-5 w-5 shrink-0 text-black" />}
              placeholder="Enter organisation email address..."
              className="h-11"
            />
          </Form.Item>
          <Form.Item
            label="Organisation Phone Number"
            name="organizationPhone"
            rules={[
              { required: false, message: "Please enter Organisation phone!" },
            ]}
            className="mb-1"
          >
            <Input
              prefix={<HiOutlinePhone className="h-5 w-5 shrink-0 text-black" />}
              placeholder="Enter organisation phone number..."
              className="h-11"
            />
          </Form.Item>
          <Form.Item
            label="Organisation Color"
            name="organizationColor"
            rules={[
              { required: true, message: "Please select Organisation color!" },
            ]}
            className="mb-1"
          >
            <Input
              prefix={<Palette className="h-5 w-5 shrink-0 text-black" />}
              type="color"
              className="relative flex !h-11 w-full min-w-0 cursor-pointer items-center overflow-hidden !py-0 !pl-11 [&_.ant-input-prefix]:pointer-events-none [&_.ant-input-prefix]:absolute [&_.ant-input-prefix]:left-3 [&_.ant-input-prefix]:top-1/2 [&_.ant-input-prefix]:z-[1] [&_.ant-input-prefix]:m-0 [&_.ant-input-prefix]:-translate-y-1/2 [&_.ant-input]:!min-h-0 [&_.ant-input]:!w-full [&_input.ant-input]:!box-border [&_input.ant-input]:!h-8 [&_input.ant-input]:!min-h-0 [&_input.ant-input]:!w-full [&_input.ant-input]:!min-w-0 [&_input.ant-input]:!max-w-none [&_input.ant-input]:!border-0 [&_input.ant-input]:!p-0 [&_input.ant-input]:!shadow-none [&_input.ant-input]:!rounded-md [&_input.ant-input::-webkit-color-swatch-wrapper]:!border-0 [&_input.ant-input::-webkit-color-swatch-wrapper]:!w-full [&_input.ant-input::-webkit-color-swatch-wrapper]:!p-0 [&_input.ant-input::-webkit-color-swatch]:!rounded-md [&_input.ant-input::-moz-color-swatch]:!border-0 [&_input.ant-input::-moz-color-swatch]:!rounded-md"
            />
          </Form.Item>
            {locationFormItem}
          </div>

          <div className="mb-4 pt-1 grid w-full grid-cols-1 gap-4 md:grid-cols-2 md:items-start">
            <Form.Item
            label="Organisation logo"
            name="organizationLogo"
            rules={[
              {
                required: false,
                message: "Please upload organisation logo!",
              },
            ]}
            className="mb-0 w-full md:row-span-2"
          >
            <div className="w-full h-full rounded-lg border border-dashed border-[#d9d9d9]">
              <label
                htmlFor="organisation-logo-input"
                className="flex h-full min-h-[164px] cursor-pointer flex-col items-center justify-center gap-2 p-4 text-center"
              >
                {fileList[0]?.url ? (
                  <>
                    <img
                      src={fileList[0].url}
                      alt="Organisation logo preview"
                      className="h-20 w-20 rounded-full object-cover"
                    />
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        handleLogoPreview(fileList[0]);
                      }}
                      className="w-full rounded-2xl bg-[#202835] text-white sm:w-[70%]"
                    >
                      Preview
                    </Button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        handleRemove();
                      }}
                      className="border-0 bg-transparent p-0 text-xs text-red-500 underline cursor-pointer"
                    >
                      Remove logo
                    </button>
                  </>
                ) : (
                  <>
                    {uploading ? (
                      <LoadingOutlined className="mx-auto text-xl" />
                    ) : (
                      <LuUpload className="mx-auto" size={24} />
                    )}
                    <span className="text-sm">Upload</span>
                  </>
                )}
              </label>
              <input
                id="organisation-logo-input"
                type="file"
                accept={ALLOWED_LOGO_EXTENSIONS.join(",")}
                className="sr-only"
                tabIndex={-1}
                onChange={handleLogoInputChange}
              />
            </div>
          </Form.Item>

          <div className="min-w-0 w-full">
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Terms & Conditions
            </label>
            <Upload
              accept=".pdf,.doc,.docx"
              fileList={legalFileList.termsDoc}
              onChange={({ fileList: next }) =>
                setLegalFileList((prev) => ({ ...prev, termsDoc: next }))
              }
              maxCount={1}
              customRequest={({ onSuccess }) => onSuccess("ok")}
              className="w-full"
            >
              <Input
                placeholder="Upload Terms & Conditions PDF"
                className="h-11 w-full cursor-pointer rounded-2xl bg-white"
                readOnly
              />
            </Upload>
          </div>
          <div className="min-w-0 w-full">
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Privacy Policy
            </label>
            <Upload
              accept=".pdf,.doc,.docx"
              fileList={legalFileList.privacyDoc}
              onChange={({ fileList: next }) =>
                setLegalFileList((prev) => ({ ...prev, privacyDoc: next }))
              }
              maxCount={1}
              customRequest={({ onSuccess }) => onSuccess("ok")}
              className="w-full"
            >
              <Input
                placeholder="Upload PDF..."
                className="h-11 w-full cursor-pointer rounded-2xl bg-white"
                readOnly
              />
            </Upload>
          </div>
          </div>
        </div>

        <Form.Item className="mb-0">
          <Button
            type="primary"
            htmlType="submit"
            block
            className="h-12 bg-[#202835] hover:bg-[#202835]/90 rounded-xl"
            loading={isCreating || isUpdating}
          >
            {editData ? "Update Organisation" : "Add Organisation"}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddOrganizationModal;
